import { useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"

import PersistDrawer from "@components/persist_drawer"
import SectionBar from "@components/section_bar"
import { PRIMARY } from "@constants/colors"
import { useAppContext } from "@contexts/app_context"
import AccountTreeIcon from "@mui/icons-material/AccountTree"
import { Assistant } from "@mui/icons-material"
import AltRouteIcon from "@mui/icons-material/AltRoute"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh"
import BiotechIcon from "@mui/icons-material/Biotech"
import HelpCenterIcon from "@mui/icons-material/HelpCenter"
import MenuIcon from "@mui/icons-material/Menu"
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined"
import ScienceIcon from "@mui/icons-material/Science"
import TerminalIcon from "@mui/icons-material/Terminal"
import TroubleshootIcon from "@mui/icons-material/Troubleshoot"
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material"
import { RoleTypes } from "__generated__/graphql"

const TABS = [
  {
    label: "AI Flow",
    path: "/ai/flow",
    icon: <AccountTreeIcon fontSize="small" />,
    adminOnly: false
  },
  {
    label: "Prompts",
    path: "/ai/prompts",
    icon: <TerminalIcon fontSize="small" />,
    adminOnly: true
  },
  {
    label: "Engine Routing",
    path: "/ai/engine",
    icon: <AltRouteIcon fontSize="small" />,
    adminOnly: true
  },
  {
    label: "Help Content",
    path: "/ai/help",
    icon: <HelpCenterIcon fontSize="small" />,
    adminOnly: true
  },
  {
    label: "AI Logs",
    path: "/ai/logs",
    icon: <Assistant fontSize="small" />,
    adminOnly: false
  },
  {
    label: "Operator Logs",
    path: "/ai/operator",
    icon: <AutoFixHighIcon fontSize="small" />,
    adminOnly: false
  },
  {
    label: "AI Simulation",
    path: "/ai/simulation",
    icon: <AutoAwesomeIcon fontSize="small" />,
    adminOnly: false
  },
  {
    label: "AI Evals",
    path: "/ai/evals",
    icon: <ScienceIcon fontSize="small" />,
    adminOnly: true
  },
  {
    label: "AI Eval Mocks",
    path: "/ai/eval-mocks",
    icon: <BiotechIcon fontSize="small" />,
    adminOnly: true
  },
  {
    label: "AI Analysis Test",
    path: "/ai/analysis-test",
    icon: <TroubleshootIcon fontSize="small" />,
    adminOnly: true
  }
]

export default function AiHubPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { me } = useAppContext()
  const [isOpen, setIsOpen] = useState(true)

  const tabs = TABS.filter(
    (tab) => !tab.adminOnly || me?.role === RoleTypes.Admin
  )

  return (
    <Box sx={{ display: "flex", height: "100%", width: "100%" }}>
      <PersistDrawer
        isOpen={isOpen}
        sx={{
          borderRight: 1,
          borderColor: "divider",
          width: "14rem",
          minWidth: "14rem",
          maxWidth: "14rem"
        }}
        header={
          <SectionBar
            sx={{ justifyContent: "space-between", alignItems: "center", p: 1 }}
          >
            <Typography p={1} variant="h6">
              AI Hub
            </Typography>
            <IconButton
              disableRipple
              sx={{ ":hover": { color: PRIMARY } }}
              onClick={() => setIsOpen(false)}
            >
              <MenuOpenOutlinedIcon />
            </IconButton>
          </SectionBar>
        }
      >
        <List dense disablePadding sx={{ pt: 1 }}>
          {tabs.map((tab) => {
            const isSelected =
              location.pathname === tab.path ||
              location.pathname.startsWith(tab.path + "/")
            return (
              <ListItemButton
                key={tab.path}
                selected={isSelected}
                onClick={() => navigate(tab.path)}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  "&.Mui-selected": {
                    bgcolor: "rgba(21, 124, 252, 0.1)",
                    "& .MuiListItemText-primary": {
                      color: PRIMARY,
                      fontWeight: 600
                    },
                    "& .MuiListItemIcon-root": { color: PRIMARY }
                  },
                  "&.Mui-selected:hover": {
                    bgcolor: "rgba(21, 124, 252, 0.15)"
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: isSelected ? PRIMARY : "text.secondary"
                  }}
                >
                  {tab.icon}
                </ListItemIcon>
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItemButton>
            )
          })}
        </List>
      </PersistDrawer>

      <Box sx={{ flex: 1, overflow: "auto", minWidth: 0, height: "100%" }}>
        {!isOpen && (
          <IconButton
            disableRipple
            size="small"
            sx={{ m: 1, ":hover": { color: PRIMARY } }}
            onClick={() => setIsOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Outlet />
      </Box>
    </Box>
  )
}
